// Timezone options - comprehensive list organized by region
export const timezoneOptions = [
  // Africa
  { label: 'Africa/Cairo (EET)', value: 'Africa/Cairo' },
  { label: 'Africa/Johannesburg (SAST)', value: 'Africa/Johannesburg' },
  { label: 'Africa/Lagos (WAT)', value: 'Africa/Lagos' },
  { label: 'Africa/Nairobi (EAT)', value: 'Africa/Nairobi' },
  
  // America
  { label: 'America/Anchorage (AKST/AKDT)', value: 'America/Anchorage' },
  { label: 'America/Bogota (COT)', value: 'America/Bogota' },
  { label: 'America/Chicago (CST/CDT)', value: 'America/Chicago' },
  { label: 'America/Denver (MST/MDT)', value: 'America/Denver' },
  { label: 'America/Los_Angeles (PST/PDT)', value: 'America/Los_Angeles' },
  { label: 'America/Mexico_City (CST/CDT)', value: 'America/Mexico_City' },
  { label: 'America/New_York (EST/EDT)', value: 'America/New_York' },
  { label: 'America/Phoenix (MST)', value: 'America/Phoenix' },
  { label: 'America/Sao_Paulo (BRT/BRST)', value: 'America/Sao_Paulo' },
  { label: 'America/Toronto (EST/EDT)', value: 'America/Toronto' },
  
  // Asia
  { label: 'Asia/Bangkok (ICT)', value: 'Asia/Bangkok' },
  { label: 'Asia/Dubai (GST)', value: 'Asia/Dubai' },
  { label: 'Asia/Hong_Kong (HKT)', value: 'Asia/Hong_Kong' },
  { label: 'Asia/Jakarta (WIB)', value: 'Asia/Jakarta' },
  { label: 'Asia/Kolkata (IST)', value: 'Asia/Kolkata' },
  { label: 'Asia/Kuala_Lumpur (MYT)', value: 'Asia/Kuala_Lumpur' },
  { label: 'Asia/Manila (PHT)', value: 'Asia/Manila' },
  { label: 'Asia/Seoul (KST)', value: 'Asia/Seoul' },
  { label: 'Asia/Shanghai (CST)', value: 'Asia/Shanghai' },
  { label: 'Asia/Singapore (SGT)', value: 'Asia/Singapore' },
  { label: 'Asia/Taipei (CST)', value: 'Asia/Taipei' },
  { label: 'Asia/Tokyo (JST)', value: 'Asia/Tokyo' },
  
  // Australia
  { label: 'Australia/Melbourne (AEST/AEDT)', value: 'Australia/Melbourne' },
  { label: 'Australia/Perth (AWST)', value: 'Australia/Perth' },
  { label: 'Australia/Sydney (AEST/AEDT)', value: 'Australia/Sydney' },
  
  // Europe
  { label: 'Europe/Amsterdam (CET/CEST)', value: 'Europe/Amsterdam' },
  { label: 'Europe/Berlin (CET/CEST)', value: 'Europe/Berlin' },
  { label: 'Europe/Brussels (CET/CEST)', value: 'Europe/Brussels' },
  { label: 'Europe/London (GMT/BST)', value: 'Europe/London' },
  { label: 'Europe/Madrid (CET/CEST)', value: 'Europe/Madrid' },
  { label: 'Europe/Moscow (MSK)', value: 'Europe/Moscow' },
  { label: 'Europe/Paris (CET/CEST)', value: 'Europe/Paris' },
  { label: 'Europe/Rome (CET/CEST)', value: 'Europe/Rome' },
  { label: 'Europe/Stockholm (CET/CEST)', value: 'Europe/Stockholm' },
  { label: 'Europe/Zurich (CET/CEST)', value: 'Europe/Zurich' },
  
  // Pacific
  { label: 'Pacific/Auckland (NZST/NZDT)', value: 'Pacific/Auckland' },
  { label: 'Pacific/Fiji (FJT/FJST)', value: 'Pacific/Fiji' },
  { label: 'Pacific/Honolulu (HST)', value: 'Pacific/Honolulu' },
];

// Helper function to format date with timezone
export const formatDateWithTimezone = (eventDate: string | Date, eventTime: string, timezone: string): string => {
  // Create ISO format date string with timezone
  let formattedDate = typeof eventDate === 'string' ? eventDate : eventDate.toISOString().split('T')[0];
  let isoDateTimeString = '';
  
  try {
    // Validate inputs before proceeding
    if (!eventDate || !eventTime || !timezone) {
      throw new Error('Missing required date, time, or timezone');
    }
    
    // Handle the case where eventDate might be a Date object or a string with timezone info
    let dateToUse;
    if (eventDate instanceof Date) {
      dateToUse = eventDate;
    } else if (typeof eventDate === 'string') {
      // Remove any timezone information if present
      const cleanDateStr = eventDate.split('T')[0];
      dateToUse = new Date(cleanDateStr);
    } else {
      throw new Error(`Unexpected event date format: ${eventDate}`);
    }
    
    // Parse time
    const timeStr = String(eventTime);
    const timeParts = timeStr.split(':');
    if (timeParts.length < 2) {
      throw new Error(`Invalid time format: ${timeStr}`);
    }
    
    const hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);
    
    // Validate time parts
    if (isNaN(hours) || isNaN(minutes)) {
      throw new Error('Invalid time format');
    }
    
    // Set the time on our date object
    dateToUse.setHours(hours);
    dateToUse.setMinutes(minutes);
    dateToUse.setSeconds(0);
    dateToUse.setMilliseconds(0);
    
    // Check if date is valid
    if (isNaN(dateToUse.getTime())) {
      throw new Error('Invalid date created');
    }
    
    // Format date for ISO string (YYYY-MM-DD)
    const year = dateToUse.getFullYear();
    const month = dateToUse.getMonth() + 1; // Month is 0-indexed
    const day = dateToUse.getDate();
    formattedDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    
    // Format time (HH:MM:SS)
    const formattedHours = dateToUse.getHours().toString().padStart(2, '0');
    const formattedMinutes = dateToUse.getMinutes().toString().padStart(2, '0');
    
    // Format the timezone string according to ISO 8601 with IANA timezone extension
    // This uses the bracket notation for IANA timezone identifiers
    const timezoneString = timezone === 'UTC' ? 'Z' : `[${timezone}]`;
    
    // Append timezone to the ISO date string
    isoDateTimeString = `${formattedDate}T${formattedHours}:${formattedMinutes}:00${timezoneString}`;
    
    return isoDateTimeString;
  } catch (error) {
    console.error('Error formatting date:', error);
    // Fall back to original date if there's an error
    return formattedDate;
  }
};
